import { useEffect, useMemo, useState } from 'react'
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom'
import { Header } from '@/components/layout/Header'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { groupService } from '@/services/groupService'
import { useAuth } from '@/auth'
import { useProfile } from '@/hooks/useProfile'
import { useDocumentHead } from '@/hooks/useDocumentHead'
import { AlertCircle, CheckCircle, Loader2, Shield, Users, LogIn, UserPlus } from 'lucide-react'

export function InviteAccept() {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const { isAuthenticated, isLoading: authLoading, login, refreshAccessToken } = useAuth()
  const { isLoading: isProfileLoading, needsCreate } = useProfile()
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [invite, setInvite] = useState<null | {
    groupId: string
    role: string
    status: string
    email?: string
    expiresAt?: string
  }>(null)
  const [groupPreview, setGroupPreview] = useState<null | {
    id: string
    name: string
    icon: string
    memberCount: number
  }>(null)
  const [groupPreviewError, setGroupPreviewError] = useState<string | null>(null)
  const [isAccepting, setIsAccepting] = useState(false)
  const [accepted, setAccepted] = useState(false)
  const [alreadyMember, setAlreadyMember] = useState(false)

  const isExpired = useMemo(() => {
    if (!invite?.expiresAt) return false
    return new Date(invite.expiresAt).getTime() < Date.now()
  }, [invite?.expiresAt])

  // Set page title - dynamic based on group info
  useDocumentHead({
    title: groupPreview?.name 
      ? `You've been invited to ${groupPreview.name}` 
      : 'Group Invitation',
    description: groupPreview 
      ? `Join ${groupPreview.name} on Badminton Leagues - ${groupPreview.memberCount} members` 
      : 'Accept your group invitation and start playing',
    image: groupPreview?.icon?.startsWith('https://') ? groupPreview.icon : undefined,
  })

  useEffect(() => {
    async function run() {
      if (!isAuthenticated) return

      if (!token) {
        setError('Invalid invite link')
        setIsLoading(false)
        return
      }

      setIsLoading(true)
      try {
        const data = await groupService.getInvite(token)
        setInvite({
          groupId: data.groupId,
          role: data.role,
          status: data.status,
          email: data.email,
          expiresAt: data.expiresAt,
        })
      } catch (e) {
        const err = e as { message?: string }
        setError(err?.message || 'Failed to load invite')
      } finally {
        setIsLoading(false)
      }
    }
    run()
  }, [token, isAuthenticated])

  useEffect(() => {
    async function loadGroupPreview() {
      if (!isAuthenticated) return
      if (!invite?.groupId) return
      setGroupPreview(null)
      setGroupPreviewError(null)
      try {
        const group = await groupService.get(invite.groupId)
        setGroupPreview({
          id: group.id,
          name: group.name,
          icon: group.icon,
          memberCount: group.memberCount,
        })
      } catch (e) {
        console.error('Failed to load group preview:', e)
        setGroupPreviewError('Could not load group details')
      }
    }
    loadGroupPreview()
  }, [invite?.groupId, isAuthenticated])

  const roleLabel = useMemo(() => {
    if (!invite?.role) return 'Member'
    return invite.role === 'admin' ? 'Admin' : 'Member'
  }, [invite?.role])

  const returnTo = useMemo(() => {
    // Preserve path + query + hash so we come back to the exact invite URL.
    return `${location.pathname}${location.search}${location.hash}`
  }, [location.pathname, location.search, location.hash])

  // If the user is logged in but still needs to create a profile, force them
  // through profile creation first (and let them come back).
  if (!authLoading && isAuthenticated && needsCreate && location.pathname !== '/signup') {
    return <Navigate to="/signup" state={{ from: location }} replace />
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-8 max-w-xl">
        <Card>
          <CardHeader>
            <CardTitle>Accept invite</CardTitle>
          </CardHeader>
          <CardContent>
            {authLoading ? (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading…
              </div>
            ) : !isAuthenticated ? (
              <div className="space-y-4">
                <div className="rounded-lg border border-border p-4 space-y-2">
                  <div className="text-sm font-medium">Log in or sign up to accept this invite</div>
                  <div className="text-sm text-muted-foreground">
                    You'll be redirected back here after you finish.
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button
                    className="w-full"
                    onClick={() => login({ returnTo })}
                  >
                    <LogIn className="h-4 w-4 mr-2" />
                    Log in
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => login({ returnTo, screenHint: 'signup' })}
                  >
                    <UserPlus className="h-4 w-4 mr-2" />
                    Sign up
                  </Button>
                </div>
              </div>
            ) : isProfileLoading ? (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading…
              </div>
            ) : isLoading ? (
              <div className="space-y-4">
                <div className="rounded-lg border border-border p-4 space-y-3">
                  <div className="flex items-start gap-3">
                    <Skeleton className="h-14 w-14 rounded-xl" />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-5 w-2/3" />
                      <div className="flex flex-wrap items-center gap-2">
                        <Skeleton className="h-3 w-24" />
                        <Skeleton className="h-3 w-28" />
                        <Skeleton className="h-3 w-20" />
                      </div>
                    </div>
                  </div>
                  <Skeleton className="h-4 w-1/2" />
                </div>
                <div className="flex gap-2">
                  <Skeleton className="h-10 w-32 rounded-md" />
                  <Skeleton className="h-10 w-24 rounded-md" />
                </div>
              </div>
            ) : error ? (
              <div className="space-y-4">
                <div className="rounded-md bg-destructive/10 border border-destructive/20 p-3 flex items-center gap-2 text-sm text-destructive">
                  <AlertCircle className="h-4 w-4" />
                  {error}
                </div>
                <Button variant="outline" onClick={() => navigate('/groups')}>
                  Go to My Groups
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {accepted || alreadyMember ? (
                  <div className="rounded-md bg-green-500/10 border border-green-500/20 p-3 flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
                    <CheckCircle className="h-4 w-4" />
                    {alreadyMember ? 'You are already a member of this group.' : 'Invite accepted!'}
                  </div>
                ) : (
                  <div className="rounded-lg border border-border p-4 space-y-3">
                    <div className="flex items-start gap-3">
                      <div className="h-14 w-14 rounded-xl bg-primary/10 flex items-center justify-center text-2xl overflow-hidden border">
                        {!groupPreview && !groupPreviewError ? (
                          <Skeleton className="h-full w-full rounded-xl" />
                        ) : groupPreview?.icon?.startsWith('data:') || groupPreview?.icon?.startsWith('https://') ? (
                          <img src={groupPreview.icon} alt={groupPreview.name} className="h-full w-full object-cover" />
                        ) : (
                          <span>{groupPreview?.icon || '🏸'}</span>
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <div className="text-base font-semibold leading-tight">
                            {groupPreview ? (
                              groupPreview.name
                            ) : groupPreviewError ? (
                              invite?.groupId
                            ) : (
                              <Skeleton className="h-5 w-48" />
                            )}
                          </div>
                          {invite?.role === 'admin' && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 dark:bg-blue-900/30 px-2 py-0.5 text-xs font-medium text-blue-700 dark:text-blue-400">
                              <Shield className="h-3 w-3" />
                              Admin invite
                            </span>
                          )}
                        </div>
                        <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                          {groupPreview && (
                            <span className="inline-flex items-center gap-1">
                              <Users className="h-3.5 w-3.5" />
                              {groupPreview.memberCount} members
                            </span>
                          )}
                          <span>Role: {roleLabel}</span>
                          {invite?.expiresAt && (
                            <span>Expires: {new Date(invite.expiresAt).toLocaleDateString('en-GB')}</span>
                          )}
                          {invite?.email && <span>For: {invite.email}</span>}
                        </div>
                        {groupPreviewError && (
                          <div className="mt-2 text-xs text-muted-foreground">{groupPreviewError}</div>
                        )}
                      </div>
                    </div>

                    {invite?.status !== 'pending' && (
                      <div className="text-sm text-destructive">This invite is {invite?.status}.</div>
                    )}
                    {isExpired && invite?.status === 'pending' && (
                      <div className="text-sm text-destructive">This invite has expired.</div>
                    )}
                  </div>
                )}
                <div className="flex gap-2">
                  {accepted || alreadyMember ? (
                    <Button onClick={() => navigate(invite?.groupId ? `/group/${invite.groupId}` : '/groups')}>
                      {invite?.groupId ? 'Go to group' : 'Go to My Groups'}
                    </Button>
                  ) : (
                    <Button
                      disabled={
                        isAccepting ||
                        !token ||
                        !invite ||
                        invite.status !== 'pending' ||
                        isExpired
                      }
                      onClick={async () => {
                        if (!token) return
                        setIsAccepting(true)
                        setError(null)
                        try {
                          const res = await groupService.acceptInvite(token)
                          setAlreadyMember(Boolean(res.alreadyMember))
                          setAccepted(true)
                          
                          // If joining as admin, refresh token to include new group in adminGroupIds
                          if (invite?.role === 'admin' && !res.alreadyMember) {
                            await refreshAccessToken()
                          }
                          
                          const target = res.groupId || invite?.groupId
                          if (target) {
                            navigate(`/group/${target}`, { replace: true })
                          } else {
                            navigate('/groups', { replace: true })
                          }
                        } catch (e) {
                          const err = e as { message?: string }
                          setError(err?.message || 'Failed to accept invite')
                        } finally {
                          setIsAccepting(false)
                        }
                      }}
                    >
                      {isAccepting ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Accepting…
                        </>
                      ) : (
                        'Accept invite'
                      )}
                    </Button>
                  )}
                  <Button variant="outline" onClick={() => navigate('/groups')}>
                    My Groups
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  )
}

